/**
 * Workshop: Agent Runner section for the settings modal.
 *
 * Mirrors production — the companion ships bundled with Denker, so there
 * is no token generation or install flow here, only the connected/loading/
 * error states.
 */

import { useState } from "react";
import { Button, Spinner } from "@/components/ui";
import { Icons } from "@/components/ui/icons";
import { cn } from "@/lib/cn";

/* ---------- Types ---------- */

type TokenState =
  | { phase: "loading" }
  | { phase: "none" }
  | { phase: "exists"; updatedAt?: string; version?: string }
  | { phase: "error"; message: string };

/* ---------- State presets for workshop exploration ---------- */

interface StatePreset {
  label: string;
  state: TokenState;
}

const STATE_PRESETS: StatePreset[] = [
  { label: "Loading", state: { phase: "loading" } },
  { label: "Not connected", state: { phase: "none" } },
  {
    label: "Connected",
    state: {
      phase: "exists",
      updatedAt: new Date().toISOString(),
      version: "1.4.2",
    },
  },
  {
    label: "Error",
    state: { phase: "error", message: "Failed to load token status" },
  },
];

/* ---------- Component ---------- */

const DEFAULT_STATE: TokenState = {
  phase: "exists",
  updatedAt: new Date().toISOString(),
  version: "1.4.2",
};

export function CompanionSection() {
  const [state, setState] = useState<TokenState>(DEFAULT_STATE);

  return (
    <div className="space-y-6" data-testid="settings-companion-section">
      {/* State selector for workshop exploration */}
      <div className="space-y-2">
        <p className="text-xs font-medium text-secondary">
          Workshop: preview each state
        </p>
        <div className="flex flex-wrap gap-1.5">
          {STATE_PRESETS.map((preset) => (
            <button
              key={preset.label}
              type="button"
              onClick={() => setState(preset.state)}
              className={cn(
                "rounded-md px-2.5 py-1 text-xs transition-colors",
                state.phase === preset.state.phase
                  ? "bg-accent/15 font-medium text-accent"
                  : "bg-btn-ghost-fill text-secondary hover:bg-btn-ghost-stroke hover:text-primary",
              )}
              data-testid={`companion-state-${preset.label.toLowerCase().replace(/\s/g, "-")}`}
            >
              {preset.label}
            </button>
          ))}
        </div>
      </div>

      <div className="h-px bg-btn-secondary-stroke" />

      {/* Agent Runner card */}
      <div className="space-y-3" data-testid="companion-token-card">
        <div>
          <h3 className="text-sm font-medium text-primary">Agent Runner</h3>
          <p className="mt-1 text-xs text-secondary">
            The Agent Runner connects your AI CLIs to this workspace. It's
            bundled with Denker and runs while the app is open — nothing
            separate to install.
          </p>
        </div>

        {state.phase === "loading" && (
          <div className="flex items-center gap-2">
            <Spinner className="h-4 w-4" />
            <span className="text-sm text-secondary">Loading...</span>
          </div>
        )}

        {state.phase === "error" && (
          <div
            className="flex items-center gap-2 text-sm text-danger"
            data-testid="companion-token-error"
          >
            <Icons.AlertTriangle className="h-4 w-4" />
            {state.message}
          </div>
        )}

        {/* phase "none" intentionally renders nothing, matching production —
            the bundled companion always has a token, so this state is
            transient and never shows extra copy. */}

        {state.phase === "exists" && (
          <div className="space-y-3" data-testid="companion-connected">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Icons.CheckCircle className="h-4 w-4 text-accent" />
                <span className="text-sm text-primary">
                  Agent Runner is installed
                </span>
                {state.version && (
                  <span className="text-xs text-muted">
                    &middot; v{state.version}
                  </span>
                )}
              </div>
              <Button
                variant="default"
                size="sm"
                data-testid="companion-agents-shortcut"
              >
                <Icons.Settings className="h-3.5 w-3.5" />
                Configure models
              </Button>
            </div>
            <p className="text-sm text-secondary">
              The Agent Runner is bundled with Denker and updates with the app —
              no separate install or token to manage.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
